import React, { useState } from "react";
import styled from "styled-components";
import { trackClickedButton } from "utils/analytics";
import { VisuallyHidden } from "components/pages/WarmUpPicker/styles";
import { SelectDropdown } from "components/shared/FilterDropdowns/FilterDropdowns";
import { classDisplayName } from "components/shared/ClassroomFilter/groupClassesBySchoolYear";
import { EditClassButton } from "./EditClassButton";
import { GroupsSkeleton } from "./GroupsSkeleton";
import { WarmUpGroupsLoadError } from "./WarmUpGroupsLoadError";
import { OneList } from "./OneList";
import { OneListCtaRow } from "./OneListCtaRow";
import { cardsFrom, filterCards, groupTypeName, groupTypeOptions } from "./oneListCards";

export const SORT_BY_OPTIONS = [
  { value: "last_updated", label: "Last Updated" },
  { value: "group_type", label: "Group Type" },
];

const labelOf = (options, value) => options.find((option) => option.value === value)?.label;

// Manage Groups with every group of the class in one list: the Filter row
// (Class, Sort By, Group Type) above the list.
export const OneListPage = ({
  classes,
  selectedClassId,
  onClassChange,
  onEditClass,
  lessons,
  quickGroups,
  warmUps,
  classroomId,
  renderLesson,
  warmUpActions,
  available,
  total,
  createMenu,
  // Every source has answered once for the class: until then only the Class
  // select and the skeleton show, so cards never reshuffle on first paint.
  settled = true,
  warmUpError = null,
  // "lesson_groups_list" | "classroom_assignment_view", for the events.
  source,
  // Sort By and Group Type, kept by the dashboard so they last across classes.
  listSettings,
  onListSettingsChange,
  // The card in focused edit ("lesson:<assignment id>"): only it shows.
  focusedKey = null,
  // Changes when a source answers a read (the list re-sorts then).
  sourcesReadAt,
  // Called when an archive leaves no card shown: focus then goes to Create
  // Groups, or to the empty state's title if the class has nothing left.
  onNoCardLeft,
  onQuickGroupsChanged,
  // CCSS: asked before a card action leaves the page with unsaved changes.
  guardNavigation,
  // The CCSS flow describes Lesson Groups in its own words.
  lessonGroupsDetail,
}) => {
  const { sortBy, groupType } = listSettings;
  const [announcement, setAnnouncement] = useState("");
  const allCards = cardsFrom({ lessons, quickGroups, warmUps });
  const typeOptions = groupTypeOptions(allCards);

  const announce = (nextSort, nextType) =>
    setAnnouncement(`${filterCards(allCards, nextType).length} groups shown, sorted by ${labelOf(SORT_BY_OPTIONS, nextSort)}`);

  const eventProps = (props) => ({ page_name: "Dashboard", class: classroomId, source, ...props });

  const changeSort = (value) => {
    if (value === sortBy) return;
    onListSettingsChange?.({ ...listSettings, sortBy: value });
    announce(value, groupType);
    trackClickedButton("Sort By", eventProps({ sort_by: labelOf(SORT_BY_OPTIONS, value) }));
  };

  const changeType = (value) => {
    if (value === groupType) return;
    onListSettingsChange?.({ ...listSettings, groupType: value });
    announce(sortBy, value);
    trackClickedButton("Group Type Filter", eventProps({ group_type: groupTypeName(value) }));
  };
  const rows = !focusedKey;
  return (
    <Page>
      {rows && (
      <FilterRow>
        <ClassGroup>
          <FilterColumn>
            <SelectDropdown
              appearance="figma"
              label="Class"
              value={selectedClassId}
              displayText={classDisplayName(classes?.find((c) => c.id === selectedClassId)) || "Select a class"}
              options={(classes || []).map((cls) => ({ value: cls.id, label: cls.name, school_year: cls.school_year }))}
              groupBySchoolYear
              onChange={onClassChange}
            />
          </FilterColumn>
          {onEditClass && <EditClassButton onClick={onEditClass} />}
        </ClassGroup>
        {settled && (
        <>
        <FilterColumn>
          <SelectDropdown
            appearance="figma"
            label="Sort By"
            value={sortBy}
            displayText={labelOf(SORT_BY_OPTIONS, sortBy)}
            options={SORT_BY_OPTIONS}
            onChange={changeSort}
          />
        </FilterColumn>
        <FilterColumn>
          <SelectDropdown
            appearance="figma"
            label="Group Type"
            value={groupType}
            displayText={labelOf(typeOptions, groupType)}
            options={typeOptions}
            onChange={changeType}
          />
        </FilterColumn>
        </>
        )}
      </FilterRow>
      )}
      {!settled && <GroupsSkeleton withHeader={false} />}
      {rows && settled && (
        <OneListCtaRow
          lessonGroupsDetail={lessonGroupsDetail}
          available={available}
          total={total}
          // Focus comes here only once no card is shown: until then the
          // archived card is still on its way out, and an emptied class hands
          // focus to its empty state instead.
          createMenu={{ ...createMenu, focusRequest: createMenu?.focusRequest && filterCards(allCards, groupType).length === 0 }}
        />
      )}
      {settled && (
      <ListArea $afterRows={rows}>
      {rows && warmUpError && (
        <WarmUpGroupsLoadError onRetry={warmUpActions?.refetchWarmUps} style={{ marginBottom: 24 }} />
      )}
      <OneList
        // Menus, dialogs and expand choices belong to one class.
        key={classroomId}
        lessons={lessons} quickGroups={quickGroups} warmUps={warmUps} classroomId={classroomId} renderLesson={renderLesson} warmUpActions={warmUpActions} sortBy={sortBy} groupType={groupType} focusedKey={focusedKey} sourcesReadAt={sourcesReadAt} onNoCardLeft={onNoCardLeft} onQuickGroupsChanged={onQuickGroupsChanged} eventProps={eventProps} guardNavigation={guardNavigation} />
      </ListArea>
      )}
      <VisuallyHidden role="status">{announcement}</VisuallyHidden>
    </Page>
  );
};

// Figma's 32px sides: GroupCodeView's 24px is taken back, so the flag-off
// pages keep theirs. The top is 8px more than the 24px every page has.
const Page = styled.div`
  display: flex;
  flex-direction: column;
  margin: 0 -24px;
  padding: 8px 32px 0;
`;

const FilterRow = styled.div`
  align-items: flex-end;
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
`;

const ClassGroup = styled.div`
  align-items: flex-end;
  display: flex;
  gap: 8px;
`;

const FilterColumn = styled.div`
  flex-shrink: 0;
  width: 200px;
`;

const ListArea = styled.div`
  margin-top: ${(p) => (p.$afterRows ? "32px" : "0")};
`;
